import { useEffect, useState } from "react";
import axios from "axios";
import { Globe, FileText, Server, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * GlobalReachSection — "From Qatar, for the World" positioning.
 * Fetches `/api/marketing/regions` once and renders an SVG map with pulsing
 * dots + per-region compliance matrix. Honest about scope — live cities
 * have an emerald dot, "ready" cities a cyan one.
 */
export default function GlobalReachSection() {
    const [regions, setRegions] = useState(null);

    useEffect(() => {
        axios.get(`${API}/marketing/regions`)
            .then(({ data }) => setRegions(data.regions || []))
            .catch(() => setRegions([])); // fall back to empty state — section still renders
    }, []);

    return (
        <section data-testid="toothmatrix-global" className="relative bg-gradient-to-br from-slate-50 via-white to-cyan-50/40 py-24 lg:py-32 overflow-hidden">
            <div aria-hidden="true" className="absolute inset-0 opacity-[0.4]" style={{
                backgroundImage: "radial-gradient(circle at 20% 30%, rgba(14,165,233,0.06), transparent 50%), radial-gradient(circle at 80% 70%, rgba(16,185,129,0.05), transparent 55%)",
            }} />
            <div className="relative max-w-7xl mx-auto px-6">
                <header className="max-w-3xl mb-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-100 border border-cyan-200 text-[11px] font-mono tracking-[0.18em] uppercase text-cyan-800 mb-5">
                        <Globe size={12} /> From Qatar · for the World
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-slate-900">
                        Designed in Doha. <span className="text-cyan-700">Ready for any clinic, anywhere.</span>
                    </h2>
                    <p className="mt-5 text-[15px] text-slate-600 leading-relaxed max-w-2xl">
                        Born from a working Qatar dental lab and engineered to the standards required by Qatar&apos;s Personal Data Privacy Protection Law — then extended to handle the compliance, currencies, and time zones of clinics from Riyadh to London to New York.
                    </p>
                </header>

                {/* WORLD MAP — pulsing dots on cities we already serve */}
                <div className="rounded-3xl bg-[#0B1120] p-6 lg:p-10 mb-12 overflow-hidden relative" data-testid="global-map">
                    <div className="grid lg:grid-cols-[1fr_280px] gap-8 items-center">
                        <div className="relative">
                            <WorldMapSVG regions={regions || []} />
                        </div>
                        <div>
                            <div className="text-[11px] font-mono tracking-[0.22em] uppercase text-cyan-300 mb-3">· Live regions</div>
                            <div className="text-[13px] text-slate-300/90 leading-relaxed mb-5">
                                {regions === null
                                    ? "Loading regions…"
                                    : `${regions.length} cities across ${new Set(regions.map((r) => r.country_name)).size} countries today, with onboarding underway in the wider Middle East, India, UK, and Europe.`}
                            </div>
                            <ul className="space-y-2 text-[13px] text-white max-h-[260px] overflow-y-auto pr-1">
                                {(regions || []).map((c) => (
                                    <li key={c.id || c.label} className="flex items-center gap-2.5">
                                        <span className={`inline-block w-2 h-2 rounded-full ${c.status === "live" ? "bg-emerald-400 shadow-[0_0_10px_2px_rgba(52,211,153,0.6)]" : "bg-cyan-400 shadow-[0_0_10px_2px_rgba(34,211,238,0.6)]"}`} />
                                        <span>{c.flag_emoji} {c.label}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* COMPLIANCE MATRIX */}
                <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden mb-10" data-testid="global-compliance-matrix">
                    <div className="px-6 py-5 border-b border-slate-200 bg-slate-50/60">
                        <div className="text-[11px] font-mono tracking-[0.18em] uppercase text-slate-600 mb-1">· Per-region compliance</div>
                        <h3 className="font-display text-2xl tracking-tight text-slate-900">Tick your box. Move on.</h3>
                        <p className="text-[13px] text-slate-500 mt-1">Each region&apos;s data-protection law is mapped explicitly to the platform&apos;s actual controls.</p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
                        {(regions || []).filter((r) => r.framework).map((r) => (
                            <ComplianceRow
                                key={r.id || r.label}
                                flag={r.flag_emoji}
                                region={r.country_name}
                                framework={r.framework}
                                mapped={r.mapped || []}
                                status={r.status}
                            />
                        ))}
                    </div>
                </div>

                {/* I18N / TIMEZONE / CURRENCY READINESS STRIP */}
                <div className="grid sm:grid-cols-3 gap-4" data-testid="global-i18n-strip">
                    <I18nCard icon={Globe} title="Multi-currency" body="QAR · AED · SAR · IQD · USD · EUR · GBP · INR · KWD · BHD · OMR. Invoices render in the clinic's local currency; FX is locked at invoice date." />
                    <I18nCard icon={FileText} title="Bilingual UI" body="English + Arabic out of the box, with right-to-left layout. Patient consent text and warranty PDFs auto-translate per clinic." />
                    <I18nCard icon={Server} title="Edge-routed delivery" body="MongoDB Atlas regions in Doha, Frankfurt, Mumbai, and Virginia. Clinic data stays in its home region — required by KSA PDPL and UAE PDPL." />
                </div>
            </div>
        </section>
    );
}

/**
 * Stylized world map with pulsing dots on each served city.
 * Uses a public-domain equirectangular world SVG as basemap (filtered into
 * the dark theme) and overlays animated dots at each region's lng/lat.
 *   x = (lng + 180) / 360 * 950
 *   y = (90  - lat) / 180 * 620
 */
function WorldMapSVG({ regions = [] }) {
    const project = (lng, lat) => ({
        x: ((Number(lng) + 180) / 360) * 950,
        y: ((90 - Number(lat)) / 180) * 620,
    });
    const hq = regions.find((r) => r.status === "live") || regions[0] || null;
    const hqPos = hq ? project(hq.lng, hq.lat) : null;

    return (
        <div className="relative w-full" style={{ aspectRatio: "950 / 620" }}>
            <img
                src="/assets/world-map.svg"
                alt="World map"
                className="absolute inset-0 w-full h-full select-none pointer-events-none"
                style={{ filter: "invert(1) brightness(0.55) sepia(0.2) hue-rotate(170deg) saturate(0.7)", opacity: 0.85 }}
            />
            <svg viewBox="0 0 950 620" className="absolute inset-0 w-full h-full" aria-hidden="true">
                <g stroke="rgba(100,116,139,0.10)" strokeWidth="0.6" strokeDasharray="2,5">
                    {[124, 248, 372, 496].map((y) => <line key={y} x1="0" y1={y} x2="950" y2={y} />)}
                    {[158, 316, 475, 633, 791].map((x) => <line key={x} x1={x} y1="0" x2={x} y2="620" />)}
                </g>

                <defs>
                    <radialGradient id="globePulse" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="rgba(34,211,238,0.7)" />
                        <stop offset="60%" stopColor="rgba(34,211,238,0.2)" />
                        <stop offset="100%" stopColor="rgba(34,211,238,0)" />
                    </radialGradient>
                    <filter id="dotGlow"><feGaussianBlur stdDeviation="3" /></filter>
                </defs>

                {hqPos && (
                    <>
                        <circle cx={hqPos.x} cy={hqPos.y} r="48" fill="url(#globePulse)">
                            <animate attributeName="r" values="22;52;22" dur="2.4s" repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0.75;0.1;0.75" dur="2.4s" repeatCount="indefinite" />
                        </circle>
                        <text x={hqPos.x + 9} y={hqPos.y - 5} fill="#22d3ee" fontFamily="JetBrains Mono, monospace" fontSize="11" fontWeight="700">
                            {(hq.label.split(",")[0] || "HQ").toUpperCase()} · HQ
                        </text>
                    </>
                )}

                {regions.map((c) => {
                    const { x, y } = project(c.lng, c.lat);
                    const isLive = c.status === "live";
                    return (
                        <g key={c.id || c.label}>
                            <circle cx={x} cy={y} r="6" fill={isLive ? "rgba(52,211,153,0.5)" : "rgba(34,211,238,0.45)"} filter="url(#dotGlow)">
                                <animate attributeName="r" values="6;11;6" dur="2.4s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0.5;0.1;0.5" dur="2.4s" repeatCount="indefinite" />
                            </circle>
                            <circle cx={x} cy={y} r="3.5" fill={isLive ? "#34d399" : "#22d3ee"} stroke="#0B1120" strokeWidth="1.5" />
                        </g>
                    );
                })}

                {hqPos && (
                    <g stroke="rgba(34,211,238,0.22)" strokeWidth="1.2" strokeDasharray="3,4" fill="none">
                        <path d={`M${hqPos.x},${hqPos.y} Q500,140 480,135`} />
                        <path d={`M${hqPos.x},${hqPos.y} Q400,180 240,180`} />
                        <path d={`M${hqPos.x},${hqPos.y} Q700,240 770,265`} />
                    </g>
                )}
            </svg>
        </div>
    );
}

function ComplianceRow({ flag, region, framework, mapped, status }) {
    return (
        <div className="p-5">
            <div className="flex items-baseline justify-between gap-3 mb-1.5">
                <div>
                    <div className="text-[20px] leading-none">{flag}</div>
                    <div className="font-display text-[15px] tracking-tight text-slate-900 mt-1">{region}</div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono tracking-wider uppercase ${status === "live" ? "bg-emerald-100 text-emerald-800" : "bg-cyan-50 text-cyan-700 border border-cyan-200"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${status === "live" ? "bg-emerald-500 animate-pulse" : "bg-cyan-500"}`} />
                    {status === "live" ? "Live" : "Ready"}
                </span>
            </div>
            <div className="text-[11.5px] font-mono tracking-wide text-cyan-700 mb-3">{framework}</div>
            <ul className="space-y-1.5 text-[12.5px] text-slate-600">
                {mapped.map((m) => (
                    <li key={m} className="flex items-start gap-1.5">
                        <Check size={12} className="text-emerald-600 mt-1 shrink-0" />
                        <span>{m}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function I18nCard({ icon: Icon, title, body }) {
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="h-10 w-10 rounded-xl bg-cyan-50 border border-cyan-200 text-cyan-700 flex items-center justify-center mb-3">
                <Icon size={18} />
            </div>
            <h4 className="font-display text-base tracking-tight text-slate-900 mb-1">{title}</h4>
            <p className="text-[12.5px] text-slate-600 leading-relaxed">{body}</p>
        </div>
    );
}
